import { HistogramBucket } from '../../types';

const MIN_PERCENTAGE = 0;
const MAX_PERCENTAGE = 100;

const bucketAssignmentCache = new Map<number, number>();
const bucketLabelCache = new Map<string, string>();
const bucketRangesCache = new Map<number, number[]>();

const roundPercentage = (value: number): number => {
  return Math.round(value * 100) / 100;
};

/**
 * Clears all caches - useful for testing
 */
export const clearHistogramCaches = (): void => {
  bucketAssignmentCache.clear();
  bucketLabelCache.clear();
  bucketRangesCache.clear();
};

const getCachedBucketRanges = (targetGrade: number): number[] | undefined => {
  const roundedGrade = Math.round(targetGrade);

  if (!bucketRangesCache.has(roundedGrade)) {
    return;
  }

  const cachedRanges = bucketRangesCache.get(roundedGrade);

  if (!cachedRanges) {
    return;
  }

  return [...cachedRanges];
};

const calculateBucketRange = (targetBaleWeights: number[], targetGrade: number): number[] => {
  if (targetBaleWeights.length === 0) {
    const roundedTarget = Math.round(targetGrade);
    return [Math.max(MIN_PERCENTAGE, roundedTarget - 5), roundedTarget, Math.min(MAX_PERCENTAGE, roundedTarget + 5)];
  }

  const percentages = targetBaleWeights.map((w) => Math.round(roundPercentage(w) * 100));
  const minData = Math.min(...percentages);
  const maxData = Math.max(...percentages);
  const spread = maxData - minData;

  let bucketWidth: number;
  if (spread <= 5) {
    bucketWidth = 1;
  } else if (spread <= 10) {
    bucketWidth = 2;
  } else if (spread <= 20) {
    bucketWidth = 3;
  } else if (spread <= 40) {
    bucketWidth = 5;
  } else {
    bucketWidth = 10;
  }

  const alignToGrid = (value: number, width: number): number => {
    return Math.floor(value / width) * width;
  };

  const minAligned = alignToGrid(minData, bucketWidth);
  const maxAligned = alignToGrid(maxData, bucketWidth);

  const buckets: number[] = [];
  for (let i = minAligned; i <= maxAligned; i += bucketWidth) {
    if (i >= MIN_PERCENTAGE && i < MAX_PERCENTAGE) {
      buckets.push(i);
    } else if (i === MAX_PERCENTAGE && buckets.length === 0) {
      buckets.push(i);
    }
  }

  const minPadding = Math.max(MIN_PERCENTAGE, minAligned - bucketWidth);

  if (minPadding < buckets[0] && minPadding >= MIN_PERCENTAGE) {
    buckets.unshift(minPadding);
  }

  if (buckets.length < 3) {
    const mid = buckets[0];
    const expand = Math.max(2, bucketWidth);
    buckets.unshift(Math.max(MIN_PERCENTAGE, mid - expand));
    buckets.push(Math.min(MAX_PERCENTAGE, mid + expand));
  }

  return buckets;
};

const findBucketForPercentage = (percentValue: number, bucketRanges: number[]): number => {
  if (bucketAssignmentCache.has(percentValue)) {
    const cachedBucket = bucketAssignmentCache.get(percentValue);
    if (cachedBucket !== undefined) {
      return cachedBucket;
    }
  }

  let assignedBucket: number = bucketRanges[bucketRanges.length - 1];

  if (percentValue < bucketRanges[0]) {
    assignedBucket = bucketRanges[0];
  } else if (percentValue >= bucketRanges[bucketRanges.length - 1]) {
    assignedBucket = bucketRanges[bucketRanges.length - 1];
  } else {
    for (let i = 0; i < bucketRanges.length - 1; i++) {
      const currentBucket = bucketRanges[i];
      const nextBucket = bucketRanges[i + 1];

      if (percentValue >= currentBucket && percentValue < nextBucket) {
        assignedBucket = currentBucket;
        break;
      }
    }
  }

  bucketAssignmentCache.set(percentValue, assignedBucket);

  return assignedBucket;
};

const countWeightsInBuckets = (
  weightGroups: Map<number, number>,
  bucketRanges: number[],
  bucketMap: Map<number, { count: number; isTarget: boolean }>
): void => {
  for (const [percentValue, count] of weightGroups.entries()) {
    const assignedBucket = findBucketForPercentage(percentValue, bucketRanges);
    const bucketData = bucketMap.get(assignedBucket);

    if (bucketData) {
      bucketData.count += count;
    }
  }
};

const getBucketLabel = (percentage: number, index: number, bucketRanges: number[], isTarget: boolean): string => {
  const bucketRangesLength = bucketRanges.length;
  const labelKey = `${percentage}_${index}_${bucketRangesLength}_${isTarget}_${percentage === MAX_PERCENTAGE}`;

  if (bucketLabelCache.has(labelKey)) {
    const cachedLabel = bucketLabelCache.get(labelKey);
    return cachedLabel !== undefined ? cachedLabel : '';
  }

  const label = formatBucketLabel(percentage, index, bucketRanges, isTarget);

  bucketLabelCache.set(labelKey, label);

  return label;
};

const formatBucketLabel = (percentage: number, index: number, bucketRanges: number[], isTarget: boolean): string => {
  const bucketRangesLength = bucketRanges.length;

  if (percentage === -1) {
    return `<${MIN_PERCENTAGE}`;
  }

  if (percentage === -2) {
    return `${MAX_PERCENTAGE}`;
  }

  if (index === 0) {
    if (bucketRangesLength > 1) {
      return `<${bucketRanges[1]}`;
    }

    return `<${percentage}`;
  }

  if (index === bucketRangesLength - 1) {
    return `>=${percentage}`;
  }

  const nextBucket = bucketRanges[index + 1];
  const rangeStart = percentage;
  const rangeEnd = nextBucket;

  return `${rangeStart}-${rangeEnd}`;
};

const groupWeightsByPercentage = (targetBaleWeights: number[]): Map<number, number> => {
  const weightGroups = new Map<number, number>();

  for (const weight of targetBaleWeights) {
    const percentValue = Math.round(roundPercentage(weight) * 100);
    weightGroups.set(percentValue, (weightGroups.get(percentValue) || 0) + 1);
  }

  return weightGroups;
};

const initializeBucketMap = (
  bucketRanges: number[],
  roundedTargetGrade: number
): Map<number, { count: number; isTarget: boolean }> => {
  const bucketMap = new Map<number, { count: number; isTarget: boolean }>();

  let closestBucket = bucketRanges[0];
  let minDistance = Math.abs(bucketRanges[0] - roundedTargetGrade);

  for (const bucket of bucketRanges) {
    const distance = Math.abs(bucket - roundedTargetGrade);
    if (distance < minDistance) {
      minDistance = distance;
      closestBucket = bucket;
    }
  }

  for (let i = 0; i < bucketRanges.length; i++) {
    const percentage = bucketRanges[i];
    bucketMap.set(percentage, {
      count: 0,
      isTarget: percentage === closestBucket,
    });
  }

  return bucketMap;
};

const generateBucketRanges = (targetBaleWeights: number[], targetGrade: number): number[] => {
  const cachedRanges = getCachedBucketRanges(targetGrade);
  if (cachedRanges) {
    return cachedRanges;
  }

  const bucketRanges = calculateBucketRange(targetBaleWeights, targetGrade);
  const roundedGrade = Math.round(targetGrade);

  bucketRangesCache.set(roundedGrade, [...bucketRanges]);

  return bucketRanges;
};

/**
 * Creates the final histogram bucket objects
 */
const createHistogramBucketObjects = (
  bucketRanges: number[],
  bucketMap: Map<number, { count: number; isTarget: boolean }>
): HistogramBucket[] => {
  const result: HistogramBucket[] = [];

  for (let i = 0; i < bucketRanges.length; i++) {
    const percentage = bucketRanges[i];
    const bucketData = bucketMap.get(percentage);

    if (!bucketData) continue;

    const label = getBucketLabel(percentage, i, bucketRanges, bucketData.isTarget);

    result.push({
      count: bucketData.count,
      isTarget: bucketData.isTarget,
      label,
      percentage,
    });
  }

  return result;
};

/**
 * Creates histogram buckets from target bale weights and target grade
 * Automatically determines optimal bucket size and range based on data distribution
 */
export const createHistogramBuckets = (
  targetBaleWeights: number[],
  targetGrade: number,
  totalBuckets: number
): HistogramBucket[] => {
  clearHistogramCaches();

  const bucketRanges = generateBucketRanges(targetBaleWeights, targetGrade);
  const roundedTargetGrade = Math.round(targetGrade);

  const bucketMap = initializeBucketMap(bucketRanges, roundedTargetGrade);

  const weightGroups = groupWeightsByPercentage(targetBaleWeights);

  countWeightsInBuckets(weightGroups, bucketRanges, bucketMap);

  return createHistogramBucketObjects(bucketRanges, bucketMap);
};
